import { supabaseBrowser } from '../supabaseBrowser';
import { subtotalFor, pricePerTicketFor } from '../pricing';

const supabase = supabaseBrowser;

// In-browser event bus for local cart mutations. Supabase realtime
// DELETE events drop the user_id filter (REPLICA IDENTITY DEFAULT only
// emits the primary key), so the Header's cart pill would stay stale
// after a removeFromCart / clearCart. Dispatching this event in the
// same tab makes the Header re-fetch immediately.
const CART_CHANGED_EVENT = 'cart:changed';
function emitCartChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
  }
}

export const cartEvents = {
  EVENT_NAME: CART_CHANGED_EVENT,
  emit: emitCartChanged,
  subscribe(handler) {
    if (typeof window === 'undefined') return () => {};
    const wrapped = () => handler();
    window.addEventListener(CART_CHANGED_EVENT, wrapped);
    return () => window.removeEventListener(CART_CHANGED_EVENT, wrapped);
  }
};

export const cartService = {
  // Get all cart items (active reservations) for a user
  async getCartItems(userId) {
    try {
      // OPTIMIZED: Select specific columns instead of *
      const { data, error } = await supabase
        ?.from('ticket_reservations')
        ?.select(`
          id,
          competition_id,
          user_id,
          number_reserved,
          expires_at,
          created_at,
          competitions (
            id,
            title,
            description,
            prize_value,
            ticket_price,
            bulk_pricing_tiers,
            instant_win_type,
            draw_date,
            competition_images (url, alt_text, display_order)
          )
        `)
        ?.eq('user_id', userId)
        ?.eq('status', 'active')
        ?.eq('reservation_type', 'cart')
        ?.gt('expires_at', new Date()?.toISOString())
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;

      return data?.map(row => ({
        reservationId: row?.id,
        competitionId: row?.competition_id,
        userId: row?.user_id,
        numberReserved: row?.number_reserved,
        expiresAt: row?.expires_at,
        createdAt: row?.created_at,
        competition: {
          id: row?.competitions?.id,
          title: row?.competitions?.title,
          description: row?.competitions?.description,
          prizeValue: row?.competitions?.prize_value,
          ticketPrice: row?.competitions?.ticket_price,
          bulkPricingTiers: Array.isArray(row?.competitions?.bulk_pricing_tiers)
            ? row.competitions.bulk_pricing_tiers
            : [],
          instantWinType: row?.competitions?.instant_win_type,
          drawDate: row?.competitions?.draw_date,
          image:
            row?.competitions?.competition_images
              ?.sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0))?.[0]?.url ||
            '/assets/images/no_image.png',
          alt:
            row?.competitions?.competition_images
              ?.sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0))?.[0]?.alt_text ||
            `${row?.competitions?.title} prize image`
        }
      })) || [];
    } catch (error) {
      console.error('Cart service error:', error);
      return [];
    }
  },

  // Get cart summary with totals
  async getCartSummary(userId) {
    try {
      const cartItems = await this.getCartItems(userId);

      if (!cartItems?.length) {
        return {
          totalItems: 0,
          totalAmount: 0,
          competitions: {},
          expiresAt: null
        };
      }

      const competitions = {};
      let earliestExpiry = null;

      // First pass: group reservations by competition and collect numbers.
      cartItems?.forEach(item => {
        const competitionId = item?.competitionId;

        if (!competitions?.[competitionId]) {
          competitions[competitionId] = {
            competition: item?.competition,
            numbers: [],
            quantity: 0,
            subtotal: 0,
            pricePerTicket: parseFloat(item?.competition?.ticketPrice || 0)
          };
        }

        competitions?.[competitionId]?.numbers?.push(item?.numberReserved);
        competitions[competitionId].quantity += 1;

        const itemExpiry = new Date(item?.expiresAt);
        if (!earliestExpiry || itemExpiry < earliestExpiry) {
          earliestExpiry = itemExpiry;
        }
      });

      // Second pass: apply bulk pricing per-competition now that we have
      // the per-comp quantity. The price-per-ticket reflects the matching
      // tier and is applied to every ticket in that competition.
      let totalAmount = 0;
      for (const competitionId of Object.keys(competitions)) {
        const c = competitions[competitionId];
        const basePrice = parseFloat(c?.competition?.ticketPrice || 0);
        const bulkTiers = c?.competition?.bulkPricingTiers || [];

        const perTicket = pricePerTicketFor({
          basePrice,
          bulkTiers,
          quantity: c.quantity
        });
        c.pricePerTicket = perTicket;
        c.subtotal = subtotalFor({
          basePrice,
          bulkTiers,
          quantity: c.quantity
        });
        totalAmount += c.subtotal;
      }

      return {
        totalItems: cartItems?.length,
        totalAmount: totalAmount,
        competitions: competitions,
        expiresAt: earliestExpiry,
        items: cartItems
      };
    } catch (error) {
      console.error('Cart summary error:', error);
      return {
        totalItems: 0,
        totalAmount: 0,
        competitions: {},
        expiresAt: null
      };
    }
  },

  // Remove item from cart
  async removeFromCart(userId, reservationId) {
    try {
      const { error } = await supabase
        ?.from('ticket_reservations')
        ?.delete()
        ?.eq('id', reservationId)
        ?.eq('user_id', userId)
        ?.eq('status', 'active');

      if (error) throw error;
      emitCartChanged();
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Clear entire cart
  async clearCart(userId) {
    try {
      const { error } = await supabase
        ?.from('ticket_reservations')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('status', 'active');

      if (error) throw error;
      emitCartChanged();
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Update quantity
  async updateQuantity(userId, competitionId, newNumbers) {
    try {
      const { competitionService } = await import('./competitionService');

      const results = await competitionService?.reserveMultipleNumbers(
        competitionId,
        userId,
        newNumbers
      );

      return results;
    } catch (error) {
      throw error;
    }
  },

  // Process checkout: hosted Cashflows for the card part, or wallet only.
  // The server works out every amount; the browser only says whether to use
  // the wallet and what total the player was shown.
  async processCheckout(userId, checkoutData) {
    try {
      const cartSummary = await this.getCartSummary(userId);

      if (!cartSummary?.items?.length) {
        throw new Error('Cart is empty');
      }

      // Promo codes the player applied, and what they take off (the server
      // checks and prices them again).
      const promoCodes = checkoutData?.promoCodes || [];
      const promoDiscount = Number(checkoutData?.promoDiscount) || 0;
      const totalAmount = Math.max(0, (cartSummary?.totalAmount || 0) - promoDiscount);
      const walletAmount = checkoutData?.useWallet ? (checkoutData?.walletAmount || 0) : 0;

      // The wallet covers the whole order: pay from the wallet, no card.
      if (walletAmount > 0 && Math.max(0, totalAmount - walletAmount) === 0) {
        return await this.processWalletOnlyCheckout(userId, { promoCodes });
      }

      if (
        checkoutData?.paymentData?.amount === undefined ||
        checkoutData?.paymentData?.amount === null
      ) {
        throw new Error('Missing payment amount for checkout.');
      }

      return await this.processCardCheckout(checkoutData);
    } catch (error) {
      console.error('Checkout processing error:', error);
      throw error;
    }
  },

  // Wallet-only checkout: one database call, as the logged-in player, which
  // prices the basket itself and takes the money from their wallet.
  async processWalletOnlyCheckout(userId, { replacePending = false, promoCodes = [] } = {}) {
    try {
      const { data: result, error: walletError } = await supabase
        ?.rpc('complete_wallet_only_order', {
          p_user_id: userId,
          p_cart_summary: { replace_pending: replacePending, promo_codes: promoCodes }
        });

      if (walletError) {
        console.error('Wallet-only checkout error:', walletError.message);
        throw new Error(`Wallet payment failed: ${walletError.message}`);
      }

      // A card payment is still open in another window.
      if (result?.error === 'payment_in_progress' && !replacePending) {
        const payFromWallet =
          typeof window !== 'undefined' &&
          window.confirm(
            `${result?.message}\n\nPress OK to pay from your wallet now (then don’t complete the card payment), or Cancel to go back to it.`
          );
        if (payFromWallet) {
          return await this.processWalletOnlyCheckout(userId, { replacePending: true, promoCodes });
        }
        if (result?.existing_action_url && typeof window !== 'undefined') {
          window.location.assign(result.existing_action_url);
          return { success: false };
        }
        throw new Error(result?.message);
      }

      if (result?.error === 'basket_changed') {
        emitCartChanged();
        throw new Error(result?.message);
      }

      if (!result?.success) {
        throw new Error(result?.message || result?.error || 'Wallet payment failed');
      }

      emitCartChanged();

      // Send the purchase-confirmation email server-side. Card orders are
      // emailed when the payment completes; wallet-only orders aren't, so
      // trigger it here. Best-effort: never block or fail checkout on it.
      try {
        await fetch('/api/wallet/notify-purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ walletTransactionId: result?.wallet_transaction_id || null }),
        });
      } catch (notifyErr) {
        console.error('Wallet purchase email trigger failed:', notifyErr?.message || notifyErr);
      }

      // Redirect to success page with wallet details
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams({
          method: 'wallet',
          amount: '0.00',
          currency: 'GBP',
          paymentref: result?.wallet_transaction_id || `WALLET-${Date.now()}`
        });

        window.location.href = `/checkout-success?${params?.toString()}`;
      }

      return {
        success: true,
        paymentType: 'wallet_only',
        transactionId: result?.wallet_transaction_id
      };

    } catch (error) {
      console.error('Wallet-only checkout error:', error);
      throw error;
    }
  },

  // Card checkout (with or without part of the wallet). The server creates
  // the order from the basket in the database and opens the Cashflows payment
  // page for the card part; the browser is then sent there.
  async processCardCheckout(checkoutData, { replacePending = false } = {}) {
    try {
      const billing = checkoutData?.paymentData?.billing || {};

      const res = await fetch('/api/cashflows/create-payment-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          useWallet: Boolean(checkoutData?.useWallet && checkoutData?.walletAmount > 0),
          expectedAmount: Number(checkoutData?.paymentData?.amount || 0)?.toFixed(2),
          billing: {
            fullName: billing?.fullName,
            email: billing?.email,
            address: billing?.address,
            city: billing?.city,
            postalCode: billing?.postalCode,
            country: billing?.country
          },
          replacePending,
          promoCodes: checkoutData?.promoCodes || []
        })
      });

      const json = await res?.json()?.catch(() => null);

      // A payment page is already open for a different basket.
      if (res?.status === 409 && json?.code === 'payment_in_progress' && !replacePending) {
        const startAgain =
          typeof window !== 'undefined' &&
          window.confirm(
            `${json?.error}\n\nPress OK to start a new payment for your basket now (then don’t complete the old one), or Cancel to go back to the payment that’s open.`
          );
        if (startAgain) {
          return await this.processCardCheckout(checkoutData, { replacePending: true });
        }
        if (json?.existingActionUrl && typeof window !== 'undefined') {
          window.location.assign(json.existingActionUrl);
          return;
        }
        throw new Error(json?.error || 'You already have a payment open.');
      }

      if (!res?.ok || !json?.success) {
        if (['basket_changed', 'amount_changed', 'basket_empty', 'wallet_covers_order'].includes(json?.code)) {
          emitCartChanged();
        }
        throw new Error(json?.error || 'Unable to start hosted payment.');
      }

      if (!json?.actionUrl) {
        throw new Error('Cashflows did not return a hosted payment URL.');
      }

      // Redirect to the Cashflows payment page
      if (typeof window !== 'undefined' && window?.location) {
        window.location?.assign(json.actionUrl);
      }

      return;

    } catch (error) {
      console.error('Cashflows checkout error:', error);
      throw error;
    }
  },

  /**
   * Subscribe to cart changes for a user
   * OPTIMIZED: Split into separate event handlers to reduce payload processing
   */
  subscribeToCart(userId, callback) {
    if (typeof window === 'undefined') {
      return () => {};
    }

    // Per-subscription channel name. Same user opening multiple tabs
    // (cart on one side, comp detail on the other) needs each tab to
    // own its own subscription — the Supabase client only honours the
    // first subscription on a given topic, so a fixed `cart_<userId>`
    // left every tab after the first silent.
    const suffix = Math.random().toString(36).slice(2, 10);
    const channel = supabase
      ?.channel(`cart_${userId}_${suffix}`)
      // Listen to INSERT events (items added to cart)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_reservations',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          if (payload?.new?.status === 'active' && payload?.new?.reservation_type === 'cart') {
            callback({
              type: 'item_added',
              item: payload?.new
            });
          }
        }
      )
      // Listen to UPDATE events (item status changed/expired).
      // Server-side filter on UPDATE/DELETE is unreliable — events can
      // silently drop. Subscribe table-wide and filter client-side.
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ticket_reservations'
        },
        (payload) => {
          if (payload?.new?.user_id !== userId && payload?.old?.user_id !== userId) return;
          if (payload?.old?.status === 'active' && payload?.new?.status !== 'active') {
            callback({
              type: 'item_expired',
              item: payload?.old
            });
          }
        }
      )
      // Listen to DELETE events (items removed from cart)
      ?.on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'ticket_reservations'
        },
        (payload) => {
          if (payload?.old?.user_id !== userId) return;
          callback({
            type: 'item_removed',
            item: payload?.old
          });
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};